export const LIVE_UPDATE_COALESCE_MS = 1_000;
export const LIVE_UPDATE_MAX_WAIT_MS = 5_000;
export const LIVE_UPDATE_JITTER_MS = 250;

interface RefreshCoordinatorOptions {
  refresh: () => Promise<void>;
  onStarted?: () => void;
  onCompleted?: () => void;
  onFailed?: () => void;
  coalesceMs?: number;
  maximumWaitMs?: number;
  jitterMs?: number;
  random?: () => number;
  initiallyVisible?: boolean;
}

export interface RefreshCoordinator {
  request(): void;
  reconcile(): void;
  setVisible(visible: boolean): void;
  dispose(): void;
}

// Coalesces event bursts and permits at most one active and one trailing refresh
export const createRefreshCoordinator = ({
  refresh,
  onStarted,
  onCompleted,
  onFailed,
  coalesceMs = LIVE_UPDATE_COALESCE_MS,
  maximumWaitMs = LIVE_UPDATE_MAX_WAIT_MS,
  jitterMs = LIVE_UPDATE_JITTER_MS,
  random = Math.random,
  initiallyVisible = true,
}: RefreshCoordinatorOptions): RefreshCoordinator => {
  let coalesceTimer: ReturnType<typeof setTimeout> | null = null;
  let maximumWaitTimer: ReturnType<typeof setTimeout> | null = null;
  // Cleared when a refresh starts. Any event received after that leaves one
  // pending refresh, including events received while this tab is hidden
  let refreshPending = false;
  let inFlight = false;
  let visible = initiallyVisible;
  let disposed = false;

  const clearTimers = (): void => {
    if (coalesceTimer) clearTimeout(coalesceTimer);
    if (maximumWaitTimer) clearTimeout(maximumWaitTimer);
    coalesceTimer = null;
    maximumWaitTimer = null;
  };

  const schedule = (): void => {
    if (disposed) return;
    if (!visible) {
      clearTimers();
      return;
    }
    if (inFlight) return;

    if (coalesceTimer) clearTimeout(coalesceTimer);
    const delay = coalesceMs + Math.floor(random() * jitterMs);
    coalesceTimer = setTimeout(() => void run(), delay);
    // Only the quiet-period timer moves. Keep the first event's deadline so a
    // continuous stream cannot postpone the refresh indefinitely.
    maximumWaitTimer ??= setTimeout(() => void run(), maximumWaitMs);
  };

  const run = async (): Promise<void> => {
    if (disposed || !refreshPending) return;
    if (!visible) {
      clearTimers();
      return;
    }
    if (inFlight) return;

    clearTimers();
    refreshPending = false;
    inFlight = true;
    onStarted?.();

    try {
      await refresh();
      if (!disposed) {
        onCompleted?.();
      }
    } catch {
      if (!disposed) {
        onFailed?.();
      }
    } finally {
      inFlight = false;
      if (refreshPending) schedule();
    }
  };

  return {
    request: () => {
      refreshPending = true;
      schedule();
    },
    reconcile: () => {
      refreshPending = true;
      void run();
    },
    setVisible: (nextVisible) => {
      visible = nextVisible;
      if (!visible) {
        clearTimers();
        return;
      }
      if (refreshPending) void run();
    },
    dispose: () => {
      disposed = true;
      clearTimers();
    },
  };
};
